import { Box, Button, Stack, useTheme } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Page } from 'theme/elements/Page';
import { FormCardSingleColumnLayout } from 'theme/elements/Forms';
import { FormButton } from 'theme/elements/Buttons';
import { useNavigate, useParams } from 'react-router-dom';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';
import { paymentPurposeForm } from './forms.types.create';
import FormCreatorComponent, { isValidForm } from 'components/FormCreatorComponent';
import { getCountries, getCurrencies, getPaymentsPurpose } from 'state/reducers/global.reducer';

import _ from 'lodash';
import { editPaymentsPurpose, getPaymentsPurpose as loadPaymentsPurpose } from 'services/global.service';
import { WarningDialog } from 'components/WarningDialog';

export const UpsertPaymentsPurpose: React.FC = () => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const dispatch = useAppDispatch();
	const [formState, setFormState] = useState<Record<string, any>>({});
	const [formValues, setFormValues] = useState<Record<string, any>>({});
	const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
	const paymentsPurpose = useAppSelector(getPaymentsPurpose);
	const navigate = useNavigate();
	const editPermissions = hasPermission([permissionEnums.EDIT_PAYMENTS_PURPOSE]);
	const prefix = 'page.payments_purpose_management.';
	const { id } = useParams();
	const countries = useAppSelector(getCountries);
	const currencies = useAppSelector(getCurrencies);

	useEffect(() => {
		if (id === 'new') {
			setFormValues({
				bank_account_country_id: '',
				code: '',
				currencies_code: '',
				payment_type: '',
				description: '',
				description_he: '',
				active: '1',
			});
		} else {
			const item = _.find(paymentsPurpose, { id: Number(id) });
			if (item) {
				setFormValues({
					...item,
					active: item.active ? '1' : '0',
				});
			}
		}
	}, [id, paymentsPurpose]);

	const form = useMemo(() => {
		const _form = { ...paymentPurposeForm };
		if (countries) {
			_form.fieldsGroups[0].fields[0].props.options = countries;
		}
		if (currencies) {
			_form.fieldsGroups[0].fields[1].props.options = currencies.map((currency) => {
				return { value: currency.code, label: currency.name };
			});
		}

		return _form;
	}, [countries, currencies]);

	const savePaymentsPurpose = () => {
		dispatch(editPaymentsPurpose(formState.values, paymentsPurpose)).then(() => {
			dispatch(loadPaymentsPurpose());
			navigate(-1);
		});
	};

	const deletePaymentsPurpose = () => {
		dispatch(editPaymentsPurpose({ id, deleted_at: new Date() }, paymentsPurpose)).then(() => {
			dispatch(loadPaymentsPurpose());
			navigate(-1);
		});
	};

	return (
		<Fragment>
			<Page includeGoBack title={t(`${prefix}${id === 'new' ? id : 'upsert'}.title`)}>
				<WarningDialog
					open={openDeleteDialog}
					subTitle={'label.is_to_delete'}
					handleCancel={() => {
						setOpenDeleteDialog(false);
						deletePaymentsPurpose();
					}}
					handleContinue={() => {
						setOpenDeleteDialog(false);
					}}
					continueLabel={t('button.continue')}
					cancelLabel={t('button.cancel')}
				/>
				<FormCardSingleColumnLayout>
					<FormCreatorComponent
						{...form}
						translation={t}
						values={formValues}
						checkInitValidation={true}
						handleChange={(newFormState: Record<string, any>) => {
							setFormState(newFormState);
						}}
					/>
					<Stack direction="row" justifyContent="space-between" width="100%">
						<FormButton>
							<Button
								onClick={() => {
									navigate(-1);
								}}
								variant="outlined"
							>
								{t('button.cancel')}
							</Button>
						</FormButton>
						{editPermissions && (
							<Stack direction="row" gap={2}>
								{id !== 'new' && (
									<FormButton>
										<Stack direction="row" gap={2}>
											<Button
												variant="contained"
												onClick={() => {
													setOpenDeleteDialog(true);
												}}
												disabled={!formState.isValid}
											>
												{t('button.delete')}
											</Button>
										</Stack>
									</FormButton>
								)}
								<FormButton>
									<Stack direction="row" gap={2}>
										<Button
											variant="contained"
											onClick={savePaymentsPurpose}
											disabled={!formState.isValid || !formState.isDirty}
										>
											{t('label.save')}
										</Button>
									</Stack>
								</FormButton>
							</Stack>
						)}
					</Stack>
				</FormCardSingleColumnLayout>
			</Page>
		</Fragment>
	);
};
